import * as React from "react";
import { useNavigate } from "react-router";

import DoneIcon from "@mui/icons-material/Done";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";

import fetchApi, {
    logFetchError,
    userFacingFetchMessage,
} from "../../../util/fetchApi";
import useDialog from "../../Dialog/useDialog";
import { aptaNumberHint, normalizeAptaNumber } from "../utils/aptaId";
import { aptaReadInit } from "../utils/requests";

type CancelButtonText = "Cancel" | "Skip for Now";

interface APTAIdDialogProps {
    cancelText: CancelButtonText;
}

export function APTAIdDialog({
    cancelText = "Cancel",
}: APTAIdDialogProps): React.JSX.Element {
    const navigate = useNavigate();
    const { onClose, setDialogType } = useDialog();
    const [aptaId, setAptaId] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);

    // Canonical `XX-XXXXX`, or null while what they have typed cannot be one.
    // Normalising here rather than at submit means a pasted number that lost
    // its hyphen enables the button instead of failing a round trip later.
    const normalizedId = normalizeAptaNumber(aptaId);

    const onSubmit = React.useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            // Registration can take seconds on a slow connection, and the
            // dialog gave no feedback while it ran - users read that as a dead
            // button and tapped again, registering several times (issue #8321).
            if (submitting) return;
            if (!normalizedId) {
                setErrorMessage(
                    "Enter your 7-digit APTA number, like 21-02826.",
                );
                return;
            }
            setSubmitting(true);
            setErrorMessage("");
            let playerId;
            try {
                const res = await fetchApi<{ playerId: string }>(
                    "/api/pti/register",
                    "POST",
                    {
                        aptaPlayerId: normalizedId,
                    },
                    // Registering verifies the number against APTA, so this is
                    // a cold read like the stats below it and cannot live
                    // inside the 10s default.
                    aptaReadInit(),
                );
                ({ playerId } = res);
            } catch (err) {
                logFetchError(err, "APTA registration failed");
                setErrorMessage(
                    userFacingFetchMessage(
                        err,
                        "We couldn't verify that APTA number right now. Please try again in a moment.",
                    ),
                );
                setSubmitting(false);
                return;
            }

            setDialogType(null);
            navigate("/player-pti", {
                state: { aptaPlayerId: parseInt(playerId, 10) },
            });
        },
        [normalizedId, navigate, setDialogType, submitting],
    );

    return (
        <>
            <DialogContent>
                <DialogContentText align="left" fontWeight="bold" fontSize={16}>
                    Link your APTA Account to receive more personalized content,
                    monitor PTI and view all your match stats.
                </DialogContentText>
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    label="APTA Number"
                    name="aptaId"
                    id="aptaId"
                    value={aptaId}
                    disabled={submitting}
                    onChange={(e) => {
                        setAptaId(e.currentTarget.value);
                        // Clear a stale rejection as soon as they start
                        // fixing it, rather than leaving the field red while
                        // they type a number that is now fine.
                        setErrorMessage("");
                    }}
                    helperText={errorMessage || aptaNumberHint(aptaId) || " "}
                    error={!!errorMessage}
                    fullWidth
                    variant="outlined"
                />
                <DialogContentText align="left">
                    To Obtain Your APTA Number:
                    <List component="ol" type="1" dense>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            Follow this link to the APTA site:&nbsp;
                            <Link
                                href="https://www.platformtennisonline.org/MemberNumberLookup.aspx"
                                target="_blank"
                            >
                                LINK
                            </Link>
                        </ListItem>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            Search for your name
                        </ListItem>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            Select your name from the results
                        </ListItem>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            Copy the APTA Number (XX-XXXXX) to the text box
                            above
                        </ListItem>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            Submit
                        </ListItem>
                        <ListItem
                            sx={{
                                display: "list-item",
                                listStyleType: "decimal",
                                listStylePosition: "inside",
                            }}
                        >
                            View your PTI stats by scrolling to the bottom of
                            the app homepage
                        </ListItem>
                    </List>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={() => onClose()}>
                    {cancelText}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={
                        submitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <DoneIcon />
                        )
                    }
                    disabled={!normalizedId || submitting}
                    onClick={onSubmit}
                >
                    {submitting ? "Submitting…" : "Submit"}
                </Button>
            </DialogActions>
        </>
    );
}

export function FirstTimeAPTAIdDialog(): React.JSX.Element {
    return <APTAIdDialog cancelText="Skip for Now" />;
}
